import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Tab, Tabs, Typography } from '@mui/material'
import { withStyles } from '@mui/styles'
import './LOC.scss'
import queryString from 'query-string'
import PropTypes from 'prop-types'
import StarRating from '../../StarRating'
import itemImage from '../../../images/loc_icon.png'
import { printPrimaryLocation } from '../ContentPages/CTHelpers/CSLHelper'
import { constants } from '../../../lib'
import { Audits, CAPAs, Findings, QualityInvestigations } from '../ContentPages'
import { UserContext } from '../../context/UserContext'
import { checkAccess, queryContent } from '../ContentPages/CTHelpers/CheckAccess'
import { CTErrorPage, makeNoAccessRows } from '../ContentPages/CTHelpers/CTErrorPage'
import Loader from '../../PageLoader'
import EmbedExternalUrl from '../../EmbedExternalUrl'
import ContentManageButton from '../contentManageButton'
import EmbedShoppingCart from '../../ShoppingCart/embedShoppingCart'
import EntityUsageTabs from '../entityUsageTabs'

// Use withStyles to build the tab other than last tab.
const StyledTab = withStyles(() => ({
  root: {
    textTransform: 'none',
    borderRight: '1px solid grey',
    fontSize: '.65rem'
  }
}))(props => <Tab disableRipple {...props} />)

// Use withStyles to build the last tab.
const LastTab = withStyles(() => ({
  root: {
    textTransform: 'none',
    fontSize: '.65rem'
  }
}))(props => <Tab disableRipple {...props} />)

// Create some style.
const contentTopStyle = {
  paddingTop: '20px'
}

// Export entire loc data.
export const LOCHome = ({ id }) => {
  const CTName = 'Local Operating Company'
  // Define the tabs.
  const pageTabs = { home: 0, audits: 1, findings: 2, qis: 3, capas: 4, associations: 5 }
  // Set data to be used.
  const [data, setData] = useState()
  // Set reference entity usage/association data.
  const [usageData, setUsageData] = useState()
  // Set loading state.
  const [isLoading, setIsLoading] = useState(true)
  // Set if have loading error.
  const [hasLoadingError, setHasLoadingError] = useState(false)
  // Set the selected tab.
  const [selectedTab, setSelectedTab] = useState(0)
  // Set if has access or not.
  const [onlyNoAccess, setOnlyNoAccess] = useState(false)
  // Set if have null value error.
  const [hasNullValue, setHasNullValue] = useState(false)
  // Set if have availability error.
  const [notAvailable, setNotAvailable] = useState()
  // Fetch the user context.
  const { userRoles, userName, otherDetails } = useContext(UserContext)
  const location = useLocation()
  const history = useNavigate()
  // Get the hash for tab.
  const parsedHash = Object.keys(queryString.parse(location.hash))
  let initialTab = 0
  if (parsedHash.length && typeof (pageTabs[parsedHash[0]]) !== 'undefined') {
    initialTab = pageTabs[parsedHash[0]]
  }
  // Handle tab change.
  const handleChange = (event, newValue) => {
    if (initialTab !== newValue) {
      initialTab = newValue
      location.hash = Object.keys(pageTabs).find(key => pageTabs[key] === newValue)
      history(location)
    }
  }

  // Set selected tab based on hash.
  useEffect(() => {
    setSelectedTab(initialTab)
  }, [initialTab])

  // Check node access when id is available.
  useEffect(() => {
    if (!id) {
      setHasNullValue(true)
      setIsLoading(false)
    } else {
      const promise = checkAccess(id)
      const show = []
      const hide = []
      promise.then(obj => {
        // Show data if have access
        if (obj.access) {
          show.push(obj.item)
        } else {
          // Hide data or generate error base on code received
          if (obj.error === 403) {
            hide.push(obj.item)
          }
          if (obj.error === 404) {
            setHasLoadingError(true)
            setIsLoading(false)
          }
        }
      }).then(() => {
        // If user have access.
        if (show && show.length > 0) {
          // Build the payload for api.
          const LOCVariable = JSON.stringify({
            id: show[0]
          })
          // Build the query URL string & call to fetch data.
          const LOCQuery = `${process.env.REACT_APP_TARGET_URL}/graphql?queryId=${constants.drupalQueryIds.LOCQuery}&variables=${LOCVariable}`
          queryContent(LOCQuery)
            .then(response => {
              if (response?.data?.nodeById?.entityLabel === null) {
                setHasLoadingError(true)
              } else {
                // Set data based on nodeQuery.
                setData(response.data)
                // Set usage/association data based on response.
                setUsageData(response.data.entityUsageContent.results[0])
              }
            })
            .catch(() => setHasLoadingError(true))
            .finally(() => setIsLoading(false))
        } else {
          if (!hide || hide.length === 0) {
            setHasLoadingError(true)
            setIsLoading(false)
          } else {
            // Generate proper user information.
            const userInfo = userName || otherDetails.name
            // Fetch no access html.
            const noAccessRows = makeNoAccessRows(CTName, hide, userInfo)
            setOnlyNoAccess(true)
            setNotAvailable(noAccessRows)
            setIsLoading(false)
          }
        }
      })
    }
  }, [id, userName, otherDetails.name])

  // Display error message if null value received.
  if (hasNullValue && !isLoading) {
    const message = <Typography dangerouslySetInnerHTML={{ __html: constants.errors.nullValue }} align='center' />
    // Return the error page.
    return (
      <CTErrorPage ct={`${CTName}`} message={message} />
    )
  }

  // Display error message if don't have access.
  if (onlyNoAccess && !isLoading && notAvailable) {
    // Return the error page.
    return (
      <CTErrorPage ct={`${CTName}`} message={notAvailable} />
    )
  }

  // Display error message if there is some loading error.
  if (hasLoadingError && !isLoading) {
    const message = <Typography dangerouslySetInnerHTML={{ __html: constants.errors.invalidContent }} align='center' />
    // Return the error page.
    return (
      <CTErrorPage ct={`${CTName}`} message={message} />
    )
  }

  // Show loader if data fetching in progress.
  if (isLoading) {
    // Return the loader.
    return <Loader />
  }

  // Return entire data for display purpose.
  // Tabs used:
  // - Home
  // - Audits
  // - Findings
  // - Quality Investigations
  // - CAPAs
  // - Associations
  // Display manage button based on user role.
  return !isLoading && data && (
    <div className='content-home tab-content-wrapper'>
      <div className='content-company-name'>
        <img className='content-image' src={itemImage} alt={data.nodeById.title} />
        <div display='flex' flex-direction='column'>
          <div className='loc-title'>
            {data.nodeById.title}
          </div>
          {data?.nodeById.starRating !== null && (<div className='loc-star-rating' display='flex' flex-direction='row'>
            <StarRating rating={Math.floor(data?.nodeById?.starRating / 2)} />
            <div className='star-rating-text'>
              {Math.floor(data?.nodeById?.starRating / 2)}/5
            </div>
          </div>)}
        </div>
        {
          userRoles.administrator ||
            userRoles.contentAuthor ||
            userRoles.feedEngineer ||
            userRoles.groupAdmin
            ? <ContentManageButton nid={id} />
            : <div />
        }
        <EmbedShoppingCart item={data.nodeById} image={itemImage} bundle="local_operating_company" />
      </div>
      <>
        <Tabs variant='fullWidth' value={selectedTab} onChange={handleChange} indicatorColor='primary'>
          <StyledTab label='Home' />
          <StyledTab label='Audits' />
          <StyledTab label='Findings' />
          <StyledTab label='Quality Investigations' />
          <StyledTab label='CAPAs' />
          <LastTab label='Associations' />
        </Tabs>
        {selectedTab === 0 &&
          <div className='content-home-wrapper content-data'>
            <div className="content-home-info--left-column">
              <div className='content-home-info--space-apart' style={contentTopStyle}>
                <Typography className="body-embed-url">
                  {data.nodeById.embedExternalUrl?.uri != null ? <EmbedExternalUrl url={data.nodeById.embedExternalUrl.uri} /> : ''}
                  {data.nodeById.body?.value != null ? <div dangerouslySetInnerHTML={{ __html: data.nodeById.body?.value }} class="fullscreen-content" /> : ''}
                </Typography>
                <Typography className='fieldCategory'>
                  Contacts
                </Typography>
                <Typography className='fieldTitle'>
                  Business Contact:
                </Typography>
                <Typography className='field'>
                  {data.nodeById.businessContact?.entity.title}
                </Typography>
                <Typography className='fieldTitle'>
                  Quality Contact:
                </Typography>
                <Typography className='field'>
                  {data.nodeById.qualityContact?.entity.title}
                </Typography>
              </div>
              <div className='content-home-info--space-apart' style={contentTopStyle}>
                <Typography className='fieldCategory'>
                  Location Information
                </Typography>
                {printPrimaryLocation(
                  {
                    title: 'External Service Provider Location',
                    address1: data.nodeById.address1,
                    address2: data.nodeById.address2,
                    city: data.nodeById.city?.entity.title,
                    state: data.nodeById.state?.entity.title,
                    country: data.nodeById.country?.entity.title,
                    lat: data.nodeById.lat,
                    lon: data.nodeById.lon
                  })}
                <Typography className='fieldTitle'>
                  Region:
                </Typography>
                <Typography className='field'>
                  {data.nodeById.region?.map(({ entity }) => entity?.title).join(', ') || constants.filter.none}
                </Typography>
                <Typography className='fieldTitle'>
                  Case Management Collaboration Networks:
                </Typography>
                <div>
                  {data.nodeById.caseNetwork?.map((cn, key) => {
                    return (
                      <Typography className='field' key={key}>
                        {cn.entity?.title}
                      </Typography>
                    )
                  })}
                </div>
                <Typography className='fieldTitle'>
                  Sectors:
                </Typography>
                <div>
                  {data.nodeById.sector?.map((sec, key) => {
                    return (
                      <Typography className='field' key={key}>
                        {sec.entity?.title}
                      </Typography>
                    )
                  })}
                </div>
                <Typography className='fieldTitle'>
                  Pharma BQ/JJRC Clusters:
                </Typography>
                <div>
                  {data.nodeById.clusters?.map((i, key) => {
                    return (
                      <Typography className='field' key={key}>
                        {i.entity?.title}
                      </Typography>
                    )
                  })}
                </div>
              </div>
              <div className='content-home-info--space-apart' style={contentTopStyle}>
                <Typography className='fieldCategory'>
                  Additional Information
                </Typography>
                <Typography className='fieldTitle'>
                  Star Rating Model
                </Typography>
                <Typography className='field'>
                  {data.nodeById.starRatingModel?.entity?.title}
                </Typography>
                <Typography className='fieldTitle'>
                  Natural Key:
                </Typography>
                <Typography className='field'>
                  {data.nodeById.naturalKey || constants.filter.none}
                </Typography>
              </div>
            </div>
            <div className='content-home-info--right-column' style={contentTopStyle}>
              <Typography className='fieldCategory'>
                Audit Countries:
              </Typography>
              {data.nodeById.auditCountries?.map(i => (
                <ul className='alternative-names__remove-style-type' key={i?.entity.tid}>{i?.entity.title}</ul>
              ))}
            </div>
          </div>}
        {selectedTab === 1 &&
          <Audits ids={data.Audits.entities?.filter(n => n).map(({ nid }) => nid).join(',')} />}
        {selectedTab === 2 &&
          <Findings ids={data.Audits?.entities?.filter(n => n).map(props => props.Findings?.entities?.filter(n => n).map(({ nid }) => nid).join(',')).join(',')} />}
        {selectedTab === 3 &&
          <QualityInvestigations ids={data.QualityInvestigations.entities?.filter(n => n).map(({ nid }) => nid).join(',')} />}
        {selectedTab === 4 &&
          <CAPAs ids={data.CAPAs.entities?.filter(n => n).map(({ nid }) => nid).join(',')} />}
        {selectedTab === 5 &&
          <EntityUsageTabs groupUsage={data.nodeById.groups} contentUsage={usageData} />}
      </>
    </div >
  )
}

// Set the loc prototype to make it required.
LOCHome.propTypes = {
  id: PropTypes.string.isRequired
}
